import { useEffect, useState } from "react";
import { Button, Card, cn, Input, Spin, Switch, Text, useToast } from "@app/ui";
import { ErrorAlert, useErrorText } from "../errors/index.js";
import { getOrgTheme, putOrgTheme } from "../api/dpp.js";
import { useT } from "../i18n.js";

/** Section ids the public passport page can hide or rename. */
const SECTION_IDS = [
  "highlights",
  "identity",
  "materials",
  "circularity",
  "use",
  "supply-chain",
  "components",
  "lca",
  "circularity-partners",
  "certifications",
  "operators",
  "documents",
  "reviews",
] as const;

/** Brand indigo — the swatch/preview fallback when no accent is set. */
const BRAND_DEFAULT = "#4746f3";

/** Curated accent presets (brand indigo first, then a cool→warm spread). */
const PRESETS = [
  "#4746f3",
  "#7c3aed",
  "#0284c7",
  "#0d9488",
  "#16a34a",
  "#d97706",
  "#e11d48",
  "#334155",
] as const;

/** Coerce free-typed hex (`0b5cad`, `#abc`, `ABC`) to a canonical `#rrggbb`, or null. */
function normalizeHex(value: string): string | null {
  const s = value.trim().replace(/^#/, "");
  if (/^[0-9a-fA-F]{6}$/.test(s)) return `#${s.toLowerCase()}`;
  if (/^[0-9a-fA-F]{3}$/.test(s)) {
    return `#${s
      .split("")
      .map((c) => c + c)
      .join("")
      .toLowerCase()}`;
  }
  return null;
}

/** White-label editor: accent color + per-section visibility and naming. */
export function ThemeCard({ orgId, canWrite }: { orgId: string; canWrite: boolean }) {
  const t = useT();
  const toast = useToast();
  const errorText = useErrorText();
  const [accentColor, setAccentColor] = useState("");
  const [disabled, setDisabled] = useState<string[]>([]);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const theme = await getOrgTheme(orgId);
        setAccentColor(theme.accentColor ?? "");
        setDisabled(theme.disabledSections ?? []);
        setLabels(theme.sectionLabels ?? {});
        setLoaded(true);
      } catch (e) {
        setError(e);
      }
    })();
  }, [orgId]);

  const save = async () => {
    setSaving(true);
    try {
      const sectionLabels = Object.fromEntries(Object.entries(labels).filter(([, v]) => v.trim() !== ""));
      const ac = accentColor.trim();
      await putOrgTheme(orgId, {
        ...(ac ? { accentColor: normalizeHex(ac) ?? ac } : {}),
        ...(disabled.length > 0 ? { disabledSections: disabled } : {}),
        ...(Object.keys(sectionLabels).length > 0 ? { sectionLabels } : {}),
      });
      toast.success(t("theme.saved"));
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  if (error != null) return <ErrorAlert error={error} />;
  if (!loaded) return <Spin />;

  const swatch = normalizeHex(accentColor) ?? BRAND_DEFAULT;
  const visibleCount = SECTION_IDS.length - disabled.filter((id) => SECTION_IDS.includes(id as never)).length;

  return (
    <Card title={t("theme.title")}>
      <div className="flex flex-col gap-7">
        <Text secondary>{t("theme.hint")}</Text>

        {/* Accent color ─ swatch + hex field + presets */}
        <div className="flex flex-col gap-3">
          <label className="text-sm font-medium">{t("theme.accent")}</label>
          <div className="flex flex-wrap items-center gap-3">
            <label
              className={cn(
                "relative inline-flex size-10 shrink-0 overflow-hidden rounded-lg border border-border shadow-xs",
                canWrite ? "cursor-pointer" : "cursor-not-allowed opacity-60",
              )}
            >
              <span className="block size-full" style={{ backgroundColor: swatch }} />
              <input
                type="color"
                value={swatch}
                disabled={!canWrite}
                onChange={(e) => setAccentColor(e.target.value)}
                aria-label={t("theme.accent")}
                className="absolute inset-0 size-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
              />
            </label>
            <div className="w-36">
              <Input
                prefix="#"
                placeholder="0b5cad"
                disabled={!canWrite}
                value={accentColor.replace(/^#/, "")}
                onChange={(e) => {
                  const hex = e.target.value.replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
                  setAccentColor(hex ? `#${hex}` : "");
                }}
              />
            </div>
            {canWrite && accentColor !== "" && (
              <Button variant="subtle" size="sm" onClick={() => setAccentColor("")}>
                {t("theme.reset")}
              </Button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {PRESETS.map((c) => (
              <button
                key={c}
                type="button"
                disabled={!canWrite}
                title={c}
                aria-label={c}
                onClick={() => setAccentColor(c)}
                style={{ backgroundColor: c }}
                className={cn(
                  "size-6 rounded-full border border-black/10 shadow-xs transition-transform",
                  canWrite ? "cursor-pointer hover:scale-110" : "cursor-not-allowed opacity-60",
                  normalizeHex(accentColor) === c && "ring-2 ring-ring ring-offset-2 ring-offset-card",
                )}
              />
            ))}
          </div>
        </div>

        {/* Sections ─ show/hide toggle + inline rename per row */}
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center justify-between gap-3">
            <label className="text-sm font-medium">{t("theme.sections")}</label>
            <span className="shrink-0 text-xs text-muted-foreground">
              {visibleCount}/{SECTION_IDS.length} {t("theme.sectionsVisible")}
            </span>
          </div>
          <div className="overflow-hidden rounded-lg border border-border">
            {SECTION_IDS.map((id, i) => {
              const visible = !disabled.includes(id);
              return (
                <div
                  key={id}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 transition-colors",
                    i > 0 && "border-t border-border",
                    visible ? "hover:bg-muted/40" : "bg-muted/30",
                  )}
                >
                  <Switch
                    checked={visible}
                    disabled={!canWrite}
                    onChange={(e) =>
                      setDisabled((prev) => (e.target.checked ? prev.filter((s) => s !== id) : [...prev, id]))
                    }
                  />
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <span
                      className={cn(
                        "truncate text-sm font-medium",
                        !visible && "text-muted-foreground line-through",
                      )}
                    >
                      {t(`themeSection.${id}`)}
                    </span>
                    {!visible && (
                      <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                        {t("theme.hidden")}
                      </span>
                    )}
                  </div>
                  <div className="w-1/3 shrink-0">
                    <Input
                      size="sm"
                      placeholder={t("theme.renamePlaceholder")}
                      disabled={!canWrite || !visible}
                      value={labels[id] ?? ""}
                      onChange={(e) => setLabels((prev) => ({ ...prev, [id]: e.target.value }))}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {canWrite && (
          <div className="flex justify-end border-t border-border pt-4">
            <Button variant="primary" loading={saving} onClick={() => void save()}>
              {t("common.save")}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
